// src/components/Platform/ProvisioningProgress.js
//
// Lifted out of Companies.js (its own AddCompanyModal success screen) so
// DemoRequests.js's own "Convert to company" flow can watch the exact same
// background ProvisionCompany call (internal/handlers/platform_company_handler.go)
// finish, without duplicating this polling logic a second time — this
// feature's own plan doc calls that reuse out explicitly. Nothing about this
// component is Companies.js-specific: it only ever polls getCompanyById and
// renders the two real checkpoints ProvisioningStatus actually reports
// (pending -> db_ready -> ready|tls_failed), same as it always did.
import { useState, useEffect } from "react";
import { getCompanyById } from "../../services/api";

// provisioningSteps turns the two real checkpoints ProvisionCompany
// actually reports into the two-stage progress list this component renders.
// Deliberately only two stages — see this file's own history in
// Companies.js before the lift: provisionTLSStep's own success branch sets
// ProvisioningStatus="ready" and Status="active" together, in the same
// step, so "issuing the certificate" and "activating the company" are truly
// one moment on the backend, not two.
function provisioningSteps(company) {
  const ps = company.provisioning_status;
  const dbDone = ps !== "pending";
  const tlsDone = ps === "ready";
  const tlsFailed = ps === "tls_failed";

  return [
    {
      key: "db",
      label: "Setting up the database & default data",
      state: dbDone ? "done" : "active",
    },
    {
      key: "tls",
      label: "Issuing the HTTPS certificate & activating",
      state: !dbDone ? "waiting" : tlsFailed ? "failed" : tlsDone ? "done" : "active",
    },
  ];
}

function StepIcon({ state }) {
  const glyph = { done: "✓", active: "●", failed: "✗", waiting: "○" }[state];
  const color = { done: "#16a34a", active: "#2563eb", failed: "#dc2626", waiting: "#cbd5e1" }[state];
  return <span style={{ color, fontWeight: 700, width: 16, display: "inline-block" }}>{glyph}</span>;
}

// ProvisioningProgress watches an EXISTING company row until its
// background provisioning finishes (or genuinely times out — see
// provisionTLSStep's own 2-minute context deadline in
// internal/platform/provision_tls.go, which is why this polls for longer
// than that). It never starts any provisioning itself — company.posflect.com
// subdomain suffix is hardcoded here the same way Companies.js's own success
// screen already hardcoded it.
export default function ProvisioningProgress({ token, companyId, initialCompany, onViewCompany }) {
  const [company, setCompany] = useState(initialCompany);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 40; // ~2 minutes at 3s each, just past provisionTLSStep's own timeout
    let timeoutId;

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await getCompanyById(token, companyId);
        if (cancelled) return;
        setCompany(res.company);
        if (res.company.provisioning_status === "ready" || res.company.provisioning_status === "tls_failed") {
          return; // done — stop scheduling further polls
        }
      } catch {
        // A transient network hiccup shouldn't stop the progress view —
        // just try again on the next tick.
      }
      if (attempts >= maxAttempts) {
        if (!cancelled) setTimedOut(true);
        return;
      }
      timeoutId = setTimeout(tick, 3000);
    };

    timeoutId = setTimeout(tick, 3000);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, companyId]);

  const steps = provisioningSteps(company);
  const finished = company.provisioning_status === "ready" || company.provisioning_status === "tls_failed";

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
        Provisioning progress
      </div>
      <div style={progressBoxStyle}>
        {steps.map((step) => (
          <div key={step.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
            <StepIcon state={step.state} />
            <span style={{ fontSize: 13, color: step.state === "waiting" ? "#94a3b8" : "#334155" }}>
              {step.label}
            </span>
          </div>
        ))}
        {company.provisioning_status === "tls_failed" && (
          <div style={{ fontSize: 12, color: "#991b1b", marginTop: 6 }}>
            {company.provisioning_error || "The HTTPS step failed."} You can open the company's page to retry,
            or activate it manually if this keeps happening (e.g. in local development).
          </div>
        )}
        {company.provisioning_status === "ready" && (
          <div style={{ fontSize: 12, color: "#166534", marginTop: 6 }}>
            {company.subdomain}.posflect.com is live.
          </div>
        )}
        {!finished && timedOut && (
          <div style={{ fontSize: 12, color: "#92400e", marginTop: 6 }}>
            Still working — this can occasionally take longer than usual. You can close this and check the
            company's own page anytime; it'll pick up right where it left off.
          </div>
        )}
        {!finished && !timedOut && (
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>Checking every few seconds…</div>
        )}
      </div>
      <button
        type="button"
        style={{ ...linkBtnStyle, marginLeft: 0, marginTop: 6 }}
        onClick={() => onViewCompany(companyId)}
      >
        View company page
      </button>
    </div>
  );
}

const progressBoxStyle = {
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "10px 12px",
};
const linkBtnStyle = {
  background: "none",
  border: "none",
  color: "#2563eb",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  marginLeft: 10,
};
